import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { TGetMessageQueuesParams, TMessageQueue } from '@/typings/api/twitter_message_queues.type'

type TGetCheckExistQueuesParams = {
  accountId: string
  actionDatetime: string
  queueId?: number
  campaignType?: string
}

type QueueList = {
  loaded: boolean
  queues: TMessageQueue[]
  params: TGetMessageQueuesParams
  isShowDetail: boolean
}

type DirectMessageDialogState = {
  campaignId: number
  prizeId: number
  queueList: QueueList
  isExistQueue: boolean
}

const createInitialState = (): DirectMessageDialogState => ({
  campaignId: 0,
  prizeId: 0,
  queueList: {
    loaded: false,
    queues: [],
    params: {
      campaignId: 0,
      prizeId: 0,
      sortBy: '-actionDatetime'
    },
    isShowDetail: false
  },
  isExistQueue: false
})

export const useTwitterDirectMessageDialogStore = defineStore('twitterDirectMessageDialogStore', () => {
  const initialState = createInitialState()

  const campaignId = ref<DirectMessageDialogState['campaignId']>(initialState.campaignId)
  const prizeId = ref<DirectMessageDialogState['prizeId']>(initialState.prizeId)
  const queueList = ref<DirectMessageDialogState['queueList']>(initialState.queueList)
  const isExistQueue = ref<DirectMessageDialogState['isExistQueue']>(initialState.isExistQueue)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    campaignId.value = nextState.campaignId
    prizeId.value = nextState.prizeId
    queueList.value = nextState.queueList
    isExistQueue.value = nextState.isExistQueue
  }

  const setCampaignId = (id: number): void => {
    campaignId.value = id
    queueList.value.params.campaignId = id
  }

  const setPrizeId = (id: number): void => {
    prizeId.value = id
    queueList.value.params.prizeId = id
  }

  const fetchQueueList = async (): Promise<void> => {
    if (!prizeId.value || !campaignId.value) {
      return
    }
    const result = await api.get('/twitter_message_queues', {
      params: queueList.value.params
    })
    queueList.value.loaded = true
    if (!result.data) {
      return
    }
    queueList.value.queues = result.data.messageQueues
  }

  const fetchCheckExistQueues = async (fetchParams: TGetCheckExistQueuesParams): Promise<void> => {
    const result = await api.get('/twitter_queues/check_exist_queues', { params: fetchParams })
    if (result.error) {
      return
    }
    isExistQueue.value = result.data
  }

  const setQueueListParams = (newParams: Partial<TGetMessageQueuesParams>): void => {
    queueList.value.params = { ...queueList.value.params, ...newParams }
  }

  const setQueueListIsShowDetail = (isShowDetail: boolean): void => {
    queueList.value.isShowDetail = isShowDetail
  }

  return {
    campaignId: readonly(campaignId),
    prizeId: readonly(prizeId),
    queueList: readonly(queueList),
    isExistQueue: readonly(isExistQueue),
    reset,
    setCampaignId,
    setPrizeId,
    fetchQueueList,
    fetchCheckExistQueues,
    setQueueListParams,
    setQueueListIsShowDetail
  }
})
